import type { ReactNode } from 'react'
import type { SportCoricoComposition, SportCoricoMatch } from '../lib/calendarSync'
import { formatMoment } from '../lib/dates'
import Tooltip, { InfoTooltip } from './Tooltip'

const average = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 })

/** « Isaac Kasonga Muanza » devient « Isaac K. » : sur le terrain, la place manque. */
function shortName(name: string): string {
  const [first = '', second] = name.split(' ')
  return second === undefined || second.length === 0 ? first : `${first} ${second[0]!.toUpperCase()}.`
}

/**
 * Ce qui se lit d'un coup d'oeil : les equipes et le score, l'homme du match
 * et la note du match. Chaque joueur cite a ses voix et, derriere une icone,
 * le nom de ceux qui ont vote pour lui.
 */
export function MatchSummary({ match }: { match: SportCoricoMatch }) {
  const { homeTeam, outsideTeam, isHome, homeScore, outsideScore, menOfTheGame, gameRatings } = match
  const hasScore = homeScore !== null && outsideScore !== null

  // Voix recues par joueur, le plus cite d'abord.
  const votersByPlayer = new Map<string, string[]>()
  for (const vote of menOfTheGame) {
    votersByPlayer.set(vote.elected, [...(votersByPlayer.get(vote.elected) ?? []), vote.voter])
  }
  const elected = [...votersByPlayer]
    .map(([player, voters]) => ({ player, count: voters.length, voters: voters.filter((voter) => voter.length > 0) }))
    .sort((left, right) => right.count - left.count || left.player.localeCompare(right.player))
  // A egalite de voix en tete, il n'y a pas encore d'homme du match : le dire, plutot que d'en nommer plusieurs.
  const top = elected[0]?.count ?? 0
  const tied = elected.filter((entry) => entry.count === top).length > 1

  if (homeTeam === null && outsideTeam === null && !hasScore && elected.length === 0 && gameRatings.length === 0) {
    return null
  }

  return (
    <div className="rounded-xl bg-neutral-100 px-4 py-3 text-center dark:bg-neutral-800">
      <p className="flex items-center justify-center gap-3 text-sm text-neutral-900 dark:text-neutral-100">
        <span className={`min-w-0 flex-1 truncate text-right ${isHome === true ? 'font-semibold' : ''}`}>
          {homeTeam ?? 'Domicile'}
        </span>
        <span className="shrink-0 text-lg font-semibold tabular-nums" aria-label="Score">
          {hasScore ? `${homeScore} – ${outsideScore}` : '–'}
        </span>
        <span className={`min-w-0 flex-1 truncate text-left ${isHome === false ? 'font-semibold' : ''}`}>
          {outsideTeam ?? 'Exterieur'}
        </span>
      </p>
      {match.competition !== null && <p className="mt-1 text-xs text-neutral-500">{match.competition}</p>}

      {elected.length > 0 && (
        <div className="mt-2 text-sm text-neutral-900 dark:text-neutral-100">
          <p className="text-xs text-neutral-500">
            Homme du match{tied ? ' : egalite' : ''} · {menOfTheGame.length} vote{menOfTheGame.length > 1 ? 's' : ''}
          </p>
          <ul aria-label="Votes pour l'homme du match">
            {elected.map((entry) => {
              const leading = entry.count === top
              return (
                // `relative` : l'infobulle des votants se centre sur la ligne.
                <li key={entry.player} className="relative flex items-center justify-center gap-1.5 py-0.5">
                  <span className={leading ? 'font-semibold' : 'text-neutral-600 dark:text-neutral-400'}>
                    {leading && !tied && <span aria-hidden="true">★ </span>}
                    {entry.player} · {entry.count} voix
                  </span>
                  {entry.voters.length > 0 && (
                    <InfoTooltip label={`Qui a vote pour ${entry.player}`} content={entry.voters.join(', ')} />
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {gameRatings.length > 0 && (
        <div className="mt-1 text-sm text-neutral-900 dark:text-neutral-100">
          <Tooltip
            content={gameRatings.map((rating, index) => (
              <span key={index} className="flex justify-between gap-3 py-0.5">
                <span>{rating.voter.length > 0 ? rating.voter : 'Anonyme'}</span>
                <span className="tabular-nums">{average.format(rating.note)}</span>
              </span>
            ))}
          >
            Note du match :{' '}
            <span className="font-semibold tabular-nums">
              {average.format(gameRatings.reduce((sum, rating) => sum + rating.note, 0) / gameRatings.length)}
            </span>{' '}
            <span className="text-neutral-500">
              ({gameRatings.length} vote{gameRatings.length > 1 ? 's' : ''})
            </span>
          </Tooltip>
        </div>
      )}
    </div>
  )
}

/** Les titulaires sur le terrain et les remplacants, chacun avec son temps de jeu s'il est connu. */
export function MatchComposition({
  composition,
  minutesOf,
}: {
  composition: SportCoricoComposition
  minutesOf: ReadonlyMap<string, number>
}) {
  if (composition.starters.length === 0 && composition.substitutes.length === 0) {
    return <p className="text-sm text-neutral-500">Les joueurs de cette composition n'ont pas pu etre lus.</p>
  }

  return (
    <>
      <Lineup starters={composition.starters} minutesOf={minutesOf} />
      {composition.substitutes.length > 0 && (
        <p className="mt-2 text-sm text-neutral-700 dark:text-neutral-300">
          <span className="font-medium">Remplacants :</span>{' '}
          {composition.substitutes.map((name) => (minutesOf.has(name) ? `${name} (${minutesOf.get(name)}')` : name)).join(', ')}
        </p>
      )}
    </>
  )
}

export function MatchLive({ match }: { match: SportCoricoMatch }) {
  const { homeTeam, outsideTeam } = match

  return (
    <ol className="space-y-1">
      {match.liveActions.map((action, index) => (
        <li key={index} className="flex items-baseline gap-2 text-sm text-neutral-900 dark:text-neutral-100">
          <span className="w-8 shrink-0 text-right text-xs tabular-nums text-neutral-500">
            {action.minute === null ? '' : `${action.minute}'`}
          </span>
          <span className="font-medium">{action.action}</span>
          {action.player !== null && (
            <span className="min-w-0 truncate text-neutral-700 dark:text-neutral-300">
              {action.player}
              {action.secondPlayer === null ? '' : ` (${action.secondPlayer})`}
              {' · '}
              {(action.isHome ? homeTeam : outsideTeam) ?? (action.isHome ? 'domicile' : 'exterieur')}
            </span>
          )}
        </li>
      ))}
    </ol>
  )
}

const scoreBox = 'inline-block min-w-6 rounded bg-neutral-100 px-1.5 py-0.5 text-center font-semibold dark:bg-neutral-800'

/** Les autres rencontres de la journee de championnat. */
export function MatchDay({ match }: { match: SportCoricoMatch }) {
  return (
    <>
      {match.competition !== null && <Heading>{match.competition}</Heading>}
      <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
        {match.otherMatches.map((other, index) => (
          <li key={index} className="flex items-center gap-3 py-2 text-sm text-neutral-900 dark:text-neutral-100">
            <span className="min-w-0 flex-1 text-right">{other.homeTeam}</span>
            <span className="shrink-0 text-center tabular-nums">
              {other.playedAt !== null && (
                <span className="block text-xs text-neutral-500">{formatMoment(other.playedAt)}</span>
              )}
              {other.homeScore !== null && other.outsideScore !== null ? (
                <span className="flex justify-center gap-1" aria-label={`${other.homeScore} – ${other.outsideScore}`}>
                  <span className={scoreBox}>{other.homeScore}</span>
                  <span className={scoreBox}>{other.outsideScore}</span>
                </span>
              ) : (
                other.playedAt === null && '–'
              )}
            </span>
            <span className="min-w-0 flex-1">{other.outsideTeam}</span>
          </li>
        ))}
      </ul>
    </>
  )
}

export function MatchRanking({ match }: { match: SportCoricoMatch }) {
  const ownTeam = match.isHome === null ? null : match.isHome ? match.homeTeam : match.outsideTeam

  return (
    <>
      {match.competition !== null && <Heading>Classement {match.competition}</Heading>}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm tabular-nums">
          <thead className="text-xs text-neutral-500">
            <tr>
              <th scope="col" className="px-2 py-1 font-medium">#</th>
              <th scope="col" className="px-2 py-1 font-medium">Equipe</th>
              <th scope="col" className="px-2 py-1 text-right font-medium">Pts</th>
              <th scope="col" className="px-2 py-1 text-right font-medium" title="Matchs joues">J</th>
              <th scope="col" className="px-2 py-1 text-center font-medium" title="Victoires, nuls, defaites">V-N-D</th>
              <th scope="col" className="px-2 py-1 text-right font-medium" title="Difference de buts">+/-</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {match.ranking.map((row) => (
              <tr
                key={`${row.rank}-${row.team}`}
                className={
                  row.team === ownTeam
                    ? 'bg-neutral-100 font-semibold text-neutral-900 dark:bg-neutral-800 dark:text-neutral-50'
                    : 'text-neutral-800 dark:text-neutral-200'
                }
              >
                <td className="px-2 py-1.5">{row.rank}</td>
                <td className="max-w-44 truncate px-2 py-1.5">{row.team}</td>
                <td className="px-2 py-1.5 text-right font-semibold">{row.points}</td>
                <td className="px-2 py-1.5 text-right">{row.played}</td>
                <td className="px-2 py-1.5 text-center">
                  {row.won}-{row.drawn}-{row.lost}
                </td>
                <td className="px-2 py-1.5 text-right">{row.difference > 0 ? `+${row.difference}` : row.difference}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

function Heading({ children }: { children: ReactNode }) {
  return (
    <p className="mb-2 text-center">
      <span className="inline-block rounded-full bg-neutral-100 px-3 py-0.5 text-xs font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
        {children}
      </span>
    </p>
  )
}

/**
 * Les titulaires a leur poste. SportCorico place chaque poste sur le terrain
 * (0 a 100, de sa propre ligne de but a celle d'en face) : l'equipe attaque
 * vers le haut. Sans ces places, la composition est rendue en liste.
 */
function Lineup({
  starters,
  minutesOf,
}: {
  starters: SportCoricoComposition['starters']
  minutesOf: ReadonlyMap<string, number>
}) {
  if (starters.some((starter) => starter.x === null || starter.y === null)) {
    return (
      <ul className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
        {starters.map((starter, index) => (
          <li key={index} className="flex items-baseline gap-2 text-sm text-neutral-900 dark:text-neutral-100">
            <span className="w-10 shrink-0 text-xs font-medium text-neutral-500">{starter.position}</span>
            <span className="truncate">{starter.name}</span>
            {minutesOf.has(starter.name) && (
              <span className="shrink-0 text-xs tabular-nums text-neutral-500">{minutesOf.get(starter.name)}'</span>
            )}
          </li>
        ))}
      </ul>
    )
  }

  const line = 'absolute border-white/40'

  return (
    // Vert fixe, en clair comme en sombre : c'est une pelouse, pas un fond de page.
    <ul aria-label="Titulaires sur le terrain" className="relative mx-auto aspect-[4/5] w-full max-w-sm overflow-hidden rounded-xl bg-emerald-700">
      <li aria-hidden="true" className={`${line} inset-2 rounded border`} />
      <li aria-hidden="true" className={`${line} inset-x-2 top-1/2 border-t`} />
      <li aria-hidden="true" className={`${line} top-1/2 left-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full border`} />
      <li aria-hidden="true" className={`${line} top-2 left-1/2 h-[13%] w-1/2 -translate-x-1/2 border border-t-0`} />
      <li aria-hidden="true" className={`${line} bottom-2 left-1/2 h-[13%] w-1/2 -translate-x-1/2 border border-b-0`} />
      {starters.map((starter, index) => (
        <li
          key={index}
          title={starter.name}
          className="absolute flex w-[5.5rem] -translate-x-1/2 translate-y-1/2 flex-col items-center text-center"
          style={{ left: `${starter.x}%`, bottom: `${starter.y}%` }}
        >
          <span className="rounded-full bg-white px-1.5 text-[11px] leading-4 font-semibold text-emerald-900">{starter.position}</span>
          <span className="mt-0.5 max-w-full truncate rounded bg-emerald-950/70 px-1 text-xs text-white">
            <span className="sr-only">{starter.name}</span>
            <span aria-hidden="true">{shortName(starter.name)}</span>
          </span>
          {minutesOf.has(starter.name) && (
            <span className="text-[11px] leading-4 tabular-nums text-emerald-50">{minutesOf.get(starter.name)}'</span>
          )}
        </li>
      ))}
    </ul>
  )
}
